// @ts-strict-ignore
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { IRootDispatch } from '../../../store/models';
import { selectors } from '../../../store/CurrentUser.model';
import { useInTest, useResponsiveEffect } from '../../../hooks';
import { hasShareBackend, ShareChannel } from '../../../modules/share';
import analytics from '../../../modules/analytics';
import { ShareModal } from './ShareModal';
import { ShareModalContainerAdapter } from './ShareMenuModal';
import { ShareProps } from './types';
import { CLIENT_REFERRALS_SHARE_CARD_UI_TEST_NAME } from './constants';
import { requireLogin } from '../../shared/login/SignUpLoginModal';
import { ShareMenuDetailsModal } from './ShareMenuDetailsModal';
import { AuthOrigins } from '../../../modules/user/constants';
import { getNativeShareState, ShareState } from './ShareState';
import nonCriticalException from '../../../modules/exceptionLogger';

export type ShareTriggerProps = ShareProps & {
  mode?: 'direct' | 'guided';
  renderTrigger: (onClick: () => void) => React.ReactNode;
  closeModalOnShare?: boolean;
  isOwnProfile?: boolean;
  onCancleOrSubmit?: () => void;
};

export const ShareTrigger: React.FCWithChildren<ShareTriggerProps> = ({
  mode,
  renderTrigger,
  closeModalOnShare,
  isOwnProfile,
  onCancleOrSubmit,
  ...props
}) => {
  const { onShareComplete } = props;
  const isLoggedIn = useSelector(selectors.isLoggedIn);
  const [shareState, setShareState] = useState<ShareState>(null);
  const is = useResponsiveEffect();
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const isDirectShareSupported = hasShareBackend() && !is.desktop;
  const inShareModalTest = useInTest(CLIENT_REFERRALS_SHARE_CARD_UI_TEST_NAME);
  const dispatch = useDispatch<IRootDispatch>();

  // Don't assign ATL-4145 until a user clicks share
  const openShareModal = useCallback(async () => {
    let inTest = false;
    // Only assign ATL-4145 if not own profile
    if (!isOwnProfile) {
      inTest = await dispatch?.referrals?.assignUpdatedShareCardTest();
    }

    const fetchedShareState = await getNativeShareState(props);
    setShareState(fetchedShareState);

    if (inTest) {
      props.onShareTrigger?.();
      setShowShareModal(true);
      analytics.track('client_profile_share_modal_viewed');
    } else if (mode === 'direct' && isDirectShareSupported && fetchedShareState.isNativeShareAvailable) {
      fetchedShareState.share();
    } else {
      props.onShareTrigger?.();
      setShowShareModal(true);
    }
  }, [
    mode,
    setShowShareModal,
    isDirectShareSupported,
    props,
    dispatch?.referrals,
    isOwnProfile,
  ]);

  const handleTriggerClick = useCallback(() => {
    // skip checking login if switch is not enabled
    if (isLoggedIn) {
      openShareModal();
    } else {
      requireLogin({
        mode: 'clientlogin',
        origin: AuthOrigins.ReferralSharing,
        helperText: 'Sign up or log in to get referral rewards',
        fullHeight: true,
      })
        .then(() => {
          openShareModal();
        })
        .catch(e => {
          // Login modal was likely closed
          nonCriticalException(e);
        });
    }
  }, [
    isLoggedIn,
    openShareModal,
  ]);

  const handleCloseModal = () => {
    setShowShareModal(false);

    props.onShareAbort?.();
    onCancleOrSubmit?.();
  };

  const handleShowShareSuccessModal = () => {
    setShowShareModal(false);
  };

  const handleShareComplete = useCallback(async (channel?: ShareChannel) => {
    if (closeModalOnShare) {
      setShowShareModal(false);
    }

    onShareComplete?.(channel);
  }, [
    setShowShareModal,
    onShareComplete,
    closeModalOnShare,
  ]);

  const handleOpenDetailsModal = () => {
    analytics.track('client_sharemodal_details_btn_clicked');
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = (eventName: string) => {
    analytics.track(eventName);
    setShowDetailsModal(false);
  };

  const performNativeShare = useCallback(() => {
    analytics.track('client_profile_share_modal_cta_clicked', {
      native_share: true,
    });

    (shareState as any)?.share();
    setShowShareModal(false);
  }, [
    shareState,
    setShowShareModal,
  ]);

  return (
    <React.Fragment>
      {renderTrigger(handleTriggerClick)}
      {showShareModal && !inShareModalTest && (
        <ShareModal
          {...props}
          onShareComplete={handleShareComplete}
          onCloseRequest={handleCloseModal}
        />
      )}
      {/** Show new UI share modal test per ATL-4145 */}
      {showShareModal && inShareModalTest && (
        <ShareModalContainerAdapter
          {...props}
          onShowShareMenuSuccessModal={handleShowShareSuccessModal}
          onShareComplete={handleShareComplete}
          closeShareModal={() => setShowShareModal(false)}
          performDirectShare={(
            isDirectShareSupported
            && shareState.isNativeShareAvailable
            && shareState.share
          ) ? performNativeShare : undefined}
          onCloseRequest={handleCloseModal}
          onDetailsPress={handleOpenDetailsModal}
        />
      )}

      {showDetailsModal && (
        <ShareMenuDetailsModal onBackPress={handleCloseDetailsModal} />
      )}
    </React.Fragment>
  );
};
